import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { DELIVERY_WAREHOUSES_GLOBAL_TAG } from '#shared/api/globalTags';
import { invalidateOnboardingCache } from '#shared/api/onboarding';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiArchiveDeliveryWarehouseByIdUrl,
    getOpenApiDeleteDeliveryWarehouseTransferUrl,
    getOpenApiGetDeliveryWarehouseMerchantShipSettingsV2Url,
    getOpenApiGetDeliveryWarehouseTransfersUrl,
    getOpenApiGetDeliveryWarehouseUrl,
    getOpenApiUpdateDeliveryWarehouseByIdUrl,
    getOpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Url,
    getOpenApiUpdateDeliveryWarehouseSettingsByIdUrl,
    getOpenApiUpdateDeliveryWarehouseTransferUrl,
    OpenApiArchiveDeliveryWarehouseByIdMethod,
    type OpenApiArchiveDeliveryWarehouseByIdPathParameters,
    type OpenApiArchiveDeliveryWarehouseByIdResponseSuccess,
    OpenApiCreateDeliveryWarehouseTransferMethod,
    type OpenApiCreateDeliveryWarehouseTransferResponses,
    OpenApiCreateDeliveryWarehouseTransferUrl,
    OpenApiDeleteDeliveryWarehouseTransferMethod,
    type OpenApiDeleteDeliveryWarehouseTransferResponseSuccess,
    OpenApiGetCourierScheduleMethod,
    type OpenApiGetCourierScheduleResponseSuccess,
    type OpenApiGetCourierScheduleSearchParams,
    OpenApiGetCourierScheduleUrl,
    OpenApiGetDeliveryWarehouseMerchantShipSettingsV2Method,
    type OpenApiGetDeliveryWarehouseMerchantShipSettingsV2PathParameters,
    type OpenApiGetDeliveryWarehouseMerchantShipSettingsV2ResponseSuccess,
    OpenApiGetDeliveryWarehouseMethod,
    type OpenApiGetDeliveryWarehousePathParameters,
    type OpenApiGetDeliveryWarehouseResponseSuccess,
    OpenApiGetDeliveryWarehousesMethod,
    type OpenApiGetDeliveryWarehousesResponseSuccess,
    type OpenApiGetDeliveryWarehousesSearchParams,
    OpenApiGetDeliveryWarehousesUrl,
    OpenApiGetDeliveryWarehouseTransfersMethod,
    type OpenApiGetDeliveryWarehouseTransfersPathParameters,
    type OpenApiGetDeliveryWarehouseTransfersResponseSuccess,
    OpenApiSearchDeliveryWarehousesMethod,
    type OpenApiSearchDeliveryWarehousesResponseSuccess,
    type OpenApiSearchDeliveryWarehousesSearchParams,
    OpenApiSearchDeliveryWarehousesUrl,
    OpenApiUpdateDeliveryWarehouseByIdMethod,
    type OpenApiUpdateDeliveryWarehouseByIdPathParameters,
    type OpenApiUpdateDeliveryWarehouseByIdRequest,
    type OpenApiUpdateDeliveryWarehouseByIdResponseSuccess,
    OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Method,
    type OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2PathParameters,
    type OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Request,
    type OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2ResponseSuccess,
    OpenApiUpdateDeliveryWarehouseSettingsByIdMethod,
    type OpenApiUpdateDeliveryWarehouseSettingsByIdPathParameters,
    type OpenApiUpdateDeliveryWarehouseSettingsByIdRequest,
    type OpenApiUpdateDeliveryWarehouseSettingsByIdResponseSuccess,
    OpenApiUpdateDeliveryWarehouseTransferMethod,
    type OpenApiUpdateDeliveryWarehouseTransferResponseSuccess,
} from '#shared/api/schema';

const WarehouseListTag = 'WarehouseList' as const;
const WarehouseTag = 'Warehouse' as const;
/**
 * Для полного сброса постраничного кэша поиска (страница редактирования склада отдельная,
 * поэтому на момент мутации у эндпоинта нет активных подписчиков — RTK Query удаляет записи,
 * и при следующем заходе список наполняется с первой страницы заново)
 */
const SearchWarehouseListTag = 'SearchWarehouseList' as const;
const WarehouseTransfersTag = 'WarehouseTransfers' as const;

const getWarehouseTransfersTags = (...warehouseIds: string[]) =>
    [...new Set(warehouseIds)].map((id) => {
        return { type: WarehouseTransfersTag, id };
    });

export const api = createApi({
    name: 'entities/warehousePoint/api/api',
    tagTypes: [
        WarehouseListTag,
        WarehouseTag,
        SearchWarehouseListTag,
        WarehouseTransfersTag,
        DELIVERY_WAREHOUSES_GLOBAL_TAG,
    ],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getDeliveryWarehouses: builder.query<
                OpenApiGetDeliveryWarehousesResponseSuccess,
                { searchParams?: OpenApiGetDeliveryWarehousesSearchParams }
            >({
                query: ({ searchParams }) => {
                    return {
                        url: OpenApiGetDeliveryWarehousesUrl,
                        method: OpenApiGetDeliveryWarehousesMethod,
                        params: searchParams,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        WarehouseListTag,
                        ...result.map((warehouse) => {
                            return { type: WarehouseTag, id: warehouse.id };
                        }),
                    ];
                },
            }),
            searchDeliveryWarehouses: builder.query<
                OpenApiSearchDeliveryWarehousesResponseSuccess,
                { searchParams?: OpenApiSearchDeliveryWarehousesSearchParams }
            >({
                query: ({ searchParams }) => {
                    return {
                        url: OpenApiSearchDeliveryWarehousesUrl,
                        method: OpenApiSearchDeliveryWarehousesMethod,
                        params: searchParams,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [SearchWarehouseListTag, DELIVERY_WAREHOUSES_GLOBAL_TAG];
                },
            }),
            getDeliveryWarehouse: builder.query<
                OpenApiGetDeliveryWarehouseResponseSuccess,
                OpenApiGetDeliveryWarehousePathParameters
            >({
                query: ({ warehouse_id }) => {
                    return {
                        url: getOpenApiGetDeliveryWarehouseUrl({ warehouse_id }),
                        method: OpenApiGetDeliveryWarehouseMethod,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [{ type: WarehouseTag, id: result.id }];
                },
            }),
            getDeliveryWarehouseTransfers: builder.query<
                OpenApiGetDeliveryWarehouseTransfersResponseSuccess,
                OpenApiGetDeliveryWarehouseTransfersPathParameters
            >({
                query: ({ warehouse_id }) => {
                    return {
                        url: getOpenApiGetDeliveryWarehouseTransfersUrl({ warehouse_id }),
                        method: OpenApiGetDeliveryWarehouseTransfersMethod,
                    };
                },
                providesTags: (result, error, { warehouse_id }) => {
                    if (error || !result) {
                        return [];
                    }

                    return getWarehouseTransfersTags(warehouse_id);
                },
            }),
            createDeliveryWarehouseTransfer: builder.mutation<
                OpenApiCreateDeliveryWarehouseTransferResponses['201']['application/json'],
                { sourceWarehouseId: string; destinationWarehouseId: string }
            >({
                query: ({ sourceWarehouseId, destinationWarehouseId }) => {
                    return {
                        url: OpenApiCreateDeliveryWarehouseTransferUrl,
                        method: OpenApiCreateDeliveryWarehouseTransferMethod,
                        body: {
                            src_warehouse_id: sourceWarehouseId,
                            dst_warehouse_id: destinationWarehouseId,
                        },
                    };
                },
                invalidatesTags: (_result, error, { sourceWarehouseId, destinationWarehouseId }) => {
                    if (error) {
                        return [];
                    }

                    return getWarehouseTransfersTags(sourceWarehouseId, destinationWarehouseId);
                },
            }),
            updateDeliveryWarehouseTransfer: builder.mutation<
                OpenApiUpdateDeliveryWarehouseTransferResponseSuccess,
                {
                    warehouseId: string;
                    oldOppositeWarehouseId: string;
                    oppositeWarehouseId: string;
                    transferId: string;
                }
            >({
                query: ({ warehouseId, oppositeWarehouseId, transferId }) => {
                    return {
                        url: getOpenApiUpdateDeliveryWarehouseTransferUrl({
                            warehouse_id: warehouseId,
                            transfer_id: transferId,
                        }),
                        method: OpenApiUpdateDeliveryWarehouseTransferMethod,
                        body: { opposite_warehouse_id: oppositeWarehouseId },
                    };
                },
                invalidatesTags: (_result, error, { warehouseId, oldOppositeWarehouseId, oppositeWarehouseId }) => {
                    if (error) {
                        return [];
                    }

                    return getWarehouseTransfersTags(warehouseId, oldOppositeWarehouseId, oppositeWarehouseId);
                },
            }),
            deleteDeliveryWarehouseTransfer: builder.mutation<
                OpenApiDeleteDeliveryWarehouseTransferResponseSuccess,
                {
                    sourceWarehouseId: string;
                    destinationWarehouseId: string;
                    transferId: string;
                }
            >({
                query: ({ sourceWarehouseId, transferId }) => {
                    return {
                        url: getOpenApiDeleteDeliveryWarehouseTransferUrl({
                            warehouse_id: sourceWarehouseId,
                            transfer_id: transferId,
                        }),
                        method: OpenApiDeleteDeliveryWarehouseTransferMethod,
                        responseType: 'text',
                    };
                },
                transformResponse: () => undefined,
                invalidatesTags: (_result, error, { sourceWarehouseId, destinationWarehouseId }) => {
                    if (error) {
                        return [];
                    }

                    return getWarehouseTransfersTags(sourceWarehouseId, destinationWarehouseId);
                },
            }),
            getDeliveryWarehouseMerchantShipSettings: builder.query<
                OpenApiGetDeliveryWarehouseMerchantShipSettingsV2ResponseSuccess,
                OpenApiGetDeliveryWarehouseMerchantShipSettingsV2PathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetDeliveryWarehouseMerchantShipSettingsV2Url(params),
                        method: OpenApiGetDeliveryWarehouseMerchantShipSettingsV2Method,
                    };
                },
            }),
            updateDeliveryWarehouseMerchantShipSettings: builder.mutation<
                OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2ResponseSuccess,
                {
                    params: OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2PathParameters;
                    payload: OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Request;
                }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Url(params),
                        method: OpenApiUpdateDeliveryWarehouseMerchantShipSettingsV2Method,
                        body: payload,
                    };
                },
                async onQueryStarted({ params }, { queryFulfilled }) {
                    const result = await queryFulfilled.catch(noop);

                    if (!result) {
                        return;
                    }

                    api.endpoints.getDeliveryWarehouseMerchantShipSettings.updateQueryData(params, () => result.data);
                    api.util.invalidateTags([{ type: WarehouseTag, id: params.warehouse_id }, SearchWarehouseListTag]);
                    invalidateOnboardingCache();
                },
            }),
            updateDeliveryWarehouse: builder.mutation<
                OpenApiUpdateDeliveryWarehouseByIdResponseSuccess,
                {
                    params: OpenApiUpdateDeliveryWarehouseByIdPathParameters;
                    payload: OpenApiUpdateDeliveryWarehouseByIdRequest;
                }
            >({
                query: ({ params: { warehouse_id }, payload }) => {
                    return {
                        url: getOpenApiUpdateDeliveryWarehouseByIdUrl({ warehouse_id }),
                        method: OpenApiUpdateDeliveryWarehouseByIdMethod,
                        body: payload,
                    };
                },
                invalidatesTags: [SearchWarehouseListTag],
                async onQueryStarted({ params: { warehouse_id } }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getDeliveryWarehouse.updateQueryData({ warehouse_id }, (draft) => {
                        if (!draft) {
                            return;
                        }

                        return data;
                    });

                    api.endpoints.getDeliveryWarehouses.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        const warehouse = draft.find((warehouse) => warehouse.id === warehouse_id);

                        if (warehouse) {
                            draft.splice(
                                draft.findIndex((warehouse) => warehouse.id === warehouse_id),
                                1,
                                data
                            );
                        }
                    });

                    invalidateOnboardingCache();
                },
            }),

            updateDeliveryWarehouseSettings: builder.mutation<
                OpenApiUpdateDeliveryWarehouseSettingsByIdResponseSuccess,
                {
                    params: OpenApiUpdateDeliveryWarehouseSettingsByIdPathParameters;
                    payload: OpenApiUpdateDeliveryWarehouseSettingsByIdRequest;
                }
            >({
                query: ({ params: { warehouse_id }, payload }) => {
                    return {
                        url: getOpenApiUpdateDeliveryWarehouseSettingsByIdUrl({ warehouse_id }),
                        method: OpenApiUpdateDeliveryWarehouseSettingsByIdMethod,
                        body: payload,
                    };
                },
                invalidatesTags: (result, error, { params: { warehouse_id } }) => {
                    if (error || !result?.is_success) {
                        return [SearchWarehouseListTag];
                    }

                    return [SearchWarehouseListTag, ...getWarehouseTransfersTags(warehouse_id)];
                },
                async onQueryStarted({ params: { warehouse_id }, payload }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    const { warehouse, is_success: isSuccess } = data;

                    if (!isSuccess) {
                        return;
                    }

                    api.endpoints.getDeliveryWarehouse.updateQueryData({ warehouse_id }, (draft) => {
                        if (!draft) {
                            return;
                        }

                        if (warehouse) {
                            return warehouse;
                        }

                        draft.settings = payload;

                        return draft;
                    });

                    api.endpoints.getDeliveryWarehouses.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        const warehouseIndex = draft.findIndex((warehouse) => warehouse.id === warehouse_id);

                        if (warehouseIndex !== -1) {
                            if (warehouse) {
                                draft.splice(warehouseIndex, 1, warehouse);
                            } else {
                                draft[warehouseIndex].settings = payload;
                            }
                        }
                    });

                    invalidateOnboardingCache();
                },
            }),
            getCourierSchedule: builder.query<
                OpenApiGetCourierScheduleResponseSuccess,
                OpenApiGetCourierScheduleSearchParams
            >({
                query: (params) => {
                    return {
                        url: OpenApiGetCourierScheduleUrl,
                        method: OpenApiGetCourierScheduleMethod,
                        params,
                    };
                },
            }),
            deleteDeliveryWarehouse: builder.mutation<
                OpenApiArchiveDeliveryWarehouseByIdResponseSuccess,
                { parameters: OpenApiArchiveDeliveryWarehouseByIdPathParameters }
            >({
                query: ({ parameters: { warehouse_id } }) => {
                    return {
                        url: getOpenApiArchiveDeliveryWarehouseByIdUrl({ warehouse_id }),
                        method: OpenApiArchiveDeliveryWarehouseByIdMethod,
                    };
                },
                invalidatesTags: [SearchWarehouseListTag],
                async onQueryStarted({ parameters: { warehouse_id: warehouseId } }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getDeliveryWarehouses.updateQueryData({}, (draft) => {
                        if (!draft) {
                            return;
                        }

                        return draft.filter((warehouse) => warehouse.id !== warehouseId);
                    });

                    invalidateOnboardingCache();
                },
            }),
        };
    },
});

export const useLazyGetDeliveryWarehouse = api.useLazyGetDeliveryWarehouseQuery;
export const getDeliveryWarehouse = api.endpoints.getDeliveryWarehouse.query;
export const useGetDeliveryWarehouses = api.useGetDeliveryWarehousesQuery;
export const getDeliveryWarehouses = api.endpoints.getDeliveryWarehouses.query;
export const useSearchDeliveryWarehousesQuery = api.useSearchDeliveryWarehousesQuery;
export const useGetDeliveryWarehouse = api.useGetDeliveryWarehouseQuery;
export const useGetDeliveryWarehouseTransfers = api.useGetDeliveryWarehouseTransfersQuery;
export const updateDeliveryWarehouse = api.endpoints.updateDeliveryWarehouse.mutate;
export const createDeliveryWarehouseTransfer = api.endpoints.createDeliveryWarehouseTransfer.mutate;
export const updateDeliveryWarehouseTransfer = api.endpoints.updateDeliveryWarehouseTransfer.mutate;
export const deleteDeliveryWarehouseTransfer = api.endpoints.deleteDeliveryWarehouseTransfer.mutate;

export const updateDeliveryWarehouseSettings = api.endpoints.updateDeliveryWarehouseSettings.mutate;

export const useLazyGetWarehouseCourierSchedule = api.useLazyGetCourierScheduleQuery;
export const getWarehouseCourierSchedule = api.endpoints.getCourierSchedule.query;

export const deleteDeliveryWarehouse = api.endpoints.deleteDeliveryWarehouse.mutate;

export const invalidateSearchWarehouseListCache = () => {
    api.util.invalidateTags([SearchWarehouseListTag]);
};

export const getDeliveryWarehouseMerchantShipSettingsCachedValue =
    api.endpoints.getDeliveryWarehouseMerchantShipSettings.getCachedValue;
export const useLazyGetDeliveryWarehouseMerchantShipSettings = api.useLazyGetDeliveryWarehouseMerchantShipSettingsQuery;
export const updateDeliveryWarehouseMerchantShipSettings =
    api.endpoints.updateDeliveryWarehouseMerchantShipSettings.mutate;
